/**
 * useAnalyticsView — the A3 module URL grammar (CD-8), a companion to useScope kept
 * separate so the scope/drill/trend grammars stay untouched. `module=<id>` selects
 * which explain/decide module the surface shows; the default (module 1, variance
 * bridges) carries no param, so the canonical `/analytics` URL stays clean — the
 * same "delete on default" shape as the Overview view tab. It reads/writes the same
 * URL as useScope/useTrend and preserves their params (URLSearchParams copy), so a
 * shared module link survives site/cadence/window changes and vice-versa. A missing
 * or malformed id resolves to the default module, never a crash.
 */

import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

const MODULE_PARAM = 'module'

export interface AnalyticsModule {
  id: string
  label: string
  /** One honest line on what the module answers — the frame renders it until built. */
  blurb: string
  /** Module 5 — blocked on the data mirror gate (A1 [[s-76ax]] / S6 [[s-2its]]). */
  gated?: boolean
}

/** The explain/decide modules, ordered by value (s-5pny). Module 1 is the default;
 *  modules 1–4 run on the single windowed /resolve (CD-7), module 5 is data-gated. */
export const ANALYTICS_MODULES: readonly AnalyticsModule[] = [
  {
    id: 'bridges',
    label: 'Variance bridges',
    blurb:
      'Waterfall decomposition of a headline delta into its drivers — Revenue − COGS − OPEX = EBITDA, exact and reconciling by construction. A DQ-tainted leg (OPEX, KNOWN-WRONG) wears its flag rather than laundering it.',
  },
  {
    id: 'pace',
    label: 'Pace',
    blurb:
      'Run-rate versus annual target, YTD pace, and a linear year-end projection with the gap to goal — the method labelled on the surface, an unserved month an honest gap rather than an interpolation.',
  },
  {
    id: 'drivers',
    label: 'Driver relationships',
    blurb:
      'Cross-metric scatter across domains that live in separate rows today — utilisation vs GP margin, OTIF slippage vs past-due growth — surfacing which operational lever predicts the money metric. Directional, not inferential.',
  },
  {
    id: 'benchmarking',
    label: 'Site benchmarking',
    blurb:
      'All sites side by side across the whole metric set, normalised to target — exposing the spread and the concentration of underperformance the one-site-at-a-time scorecard tabs hide.',
  },
  {
    id: 'contribution',
    label: 'Contribution',
    blurb:
      'Top contributors read from the reconciled drill trees — the customers and products driving the past-due £, the product lines carrying versus dragging GP. Reconciles to the parent cell by construction.',
    gated: true,
  },
]

const DEFAULT_MODULE = ANALYTICS_MODULES[0]

export interface AnalyticsView {
  module: AnalyticsModule
  modules: readonly AnalyticsModule[]
  setModule: (id: string) => void
}

export function useAnalyticsView(): AnalyticsView {
  const [searchParams, setSearchParams] = useSearchParams()
  const raw = searchParams.get(MODULE_PARAM)
  const module = useMemo(
    () => ANALYTICS_MODULES.find((candidate) => candidate.id === raw) ?? DEFAULT_MODULE,
    [raw]
  )
  const setModule = useCallback(
    (id: string) =>
      setSearchParams(
        (previous) => {
          const params = new URLSearchParams(previous)
          // Default module carries no param — keeps the canonical URL clean, and any
          // unknown id falls through to the default on read.
          if (id === DEFAULT_MODULE.id) params.delete(MODULE_PARAM)
          else params.set(MODULE_PARAM, id)
          return params
        },
        { replace: true }
      ),
    [setSearchParams]
  )
  return { module, modules: ANALYTICS_MODULES, setModule }
}
